import React, { useState } from "react";
import { Button, Tab, Tabs, Form } from "react-bootstrap";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from "@chakra-ui/react";
import { useGetDta } from "../../hooks/useGetDta";
import Spinner from "../Spinners/Spinner";

/**
 * Lista de plantillas y respuestas rápidas
 *
 * @param {Object} props - Propiedades del componente
 * @param {Boolean} props.show - Indica si se muestra el componente
 * @param {Function} props.setShow - Función para mostrar u ocultar el componente
 * @param {Function} props.setFormValue - Función para establecer el valor del formulario
 * @param {String} props.socialNetwork - Red social seleccionada
 * @param {Boolean} props.isBlocked - Indica si el chat está bloqueado
 * @returns {JSX.Element} Componente de React
 */
const TemplatesList = ({
  show,
  setShow,
  setFormValue,
  socialNetwork,
  isBlocked,
}) => {
  const [expandedItemId, setExpandedItemId] = useState(null); // ID del item expandido
  const [searchValue, setSearchValue] = useState(""); // Valor del campo de búsqueda

  // Peticiones para obtener las respuestas rápidas y las plantillas
  const { loading: quickResponsesLoading, data: quickResponsesData } =
    useGetDta("respuestas/respuesta");
  const { loading: templatesLoading, data: templatesData } = useGetDta(
    "respuestas/templates"
  );

  /**
   * Maneja la expansión de un item
   * Si el item está expandido, lo contrae
   *
   * @param {Number} itemId - ID del item
   * @returns {void}
   */
  const handleExpand = (itemId) =>
    expandedItemId === itemId
      ? setExpandedItemId(null)
      : setExpandedItemId(itemId);

  /**
   * Establece el valor del formulario
   * Si la respuesta tiene una foto, la establece
   *
   * @param {Object} pay - Datos de la respuesta
   * @returns {void}
   */
  const setR = (pay) =>
    pay.foto.length > 0
      ? setFormValue({ newMessage: pay.sms, photo: pay.foto })
      : setFormValue({ newMessage: pay.sms });

  /**
   * Maneja el cambio en el campo de búsqueda
   *
   * @param {Object} e - Evento del campo de búsqueda
   * @returns {void}
   */
  const handleSearchChange = (e) => setSearchValue(e.target.value);
  return (
    <Popover zIndex={100} placement="top">
      {/* Botón para mostrar la lista de respuestas */}
      <PopoverTrigger>
        <Button
          className="btn-light w-100 px-1"
          onClick={() => {
            setShow(!show);
          }}
          style={{ border: "none" }}
        >
          <i className="uil uil-comment-dots font-16" />
        </Button>
      </PopoverTrigger>

      {/* Contenido de la lista de respuestas */}
      <PopoverContent
        rounded="lg"
        className="bg-answer p-2 rounded"
        style={{
          width: 400,
          height: 400,
          position: "absolute",
          zIndex: 100,
          left: -100,
          bottom: 5,
        }}
      >
        <PopoverBody>
          <Tabs defaultActiveKey="quickResponses" variant="pills" fill>
            {/* Pestaña de respuestas rápidas */}
            <Tab eventKey="quickResponses" title="Respuestas">
              <div style={{ width: 280 }} className="mx-auto my-1 p-1">
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  style={{
                    borderRadius: "15px",
                    padding: "10px",
                  }}
                />
              </div>

              <div style={{ maxHeight: "270px", overflowY: "auto" }}>
                {quickResponsesLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "270px" }}
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 px-1 py-0">
                    {quickResponsesData
                      .filter(
                        (item) =>
                          item.sms &&
                          item.name &&
                          (item.sms
                            .toLowerCase()
                            .includes(searchValue.toLowerCase()) ||
                            item.name
                              .toLowerCase()
                              .includes(searchValue.toLowerCase()))
                      )
                      .map((item) => (
                        <div
                          key={item.id}
                          onDoubleClick={() => {
                            if (!isBlocked) {
                              setR({ sms: item.sms, foto: item.foto });
                            }
                          }}
                          className="cursor-pointer resp-item-bg rounded p-2 my-2 px-3"
                          style={{
                            maxWidth: "400px",
                          }}
                        >
                          <div className="flex flex-col-reverse items-start justify-between mb-2">
                            <div style={{ maxWidth: "450px" }}>
                              <h5>{item.name}</h5>
                              <p>
                                {expandedItemId === item.id
                                  ? item.sms
                                  : item.sms.slice(0, 60) + "..."}
                              </p>

                              <p
                                className="cursor-pointer text-primary"
                                onClick={() => handleExpand(item.id)}
                              >
                                {expandedItemId === item.id
                                  ? "Ver menos"
                                  : "Ver más"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Tab>

            {/* Pestaña de plantillas */}
            <Tab eventKey="templates" title="Plantillas">
              <div style={{ width: 280 }} className="mx-auto my-1 p-1">
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  style={{
                    borderRadius: "15px",
                    padding: "10px",
                  }}
                />
              </div>

              <div style={{ maxHeight: "270px", overflowY: "auto" }}>
                {templatesLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "270px" }}
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 p-1 px-1 py-0">
                    {socialNetwork === "telegram" ||
                    socialNetwork === "messenger" ||
                    socialNetwork === "instagram" ? (
                      <h5 className="text-center">
                        No hay plantillas disponibles
                      </h5>
                    ) : (
                      templatesData
                        .filter(
                          (item) =>
                            item.sms &&
                            item.sms
                              .toLowerCase()
                              .includes(searchValue.toLowerCase())
                        )
                        .map((item, i) => (
                          <div key={i}>
                            {socialNetwork === item.app && (
                              <div
                                onDoubleClick={() =>
                                  setFormValue({
                                    newMessage: item.sms,
                                    photo: item.foto,
                                    tag: "template",
                                    idtem: item.id,
                                  })
                                }
                                className="cursor-pointer resp-item-bg rounded p-2 my-2 px-3"
                                style={{
                                  maxWidth: "400px",
                                }}
                              >
                                <div className="flex flex-col-reverse items-start justify-between mb-2">
                                  <div>
                                    <h5>{item.name}</h5>
                                    <p>
                                      {expandedItemId === item.id
                                        ? item.sms
                                        : item.sms.slice(0, 60) + "..."}
                                    </p>
                                  </div>
                                  <p
                                    className="cursor-pointer text-primary"
                                    onClick={() => handleExpand(item.id)}
                                  >
                                    {expandedItemId === item.id
                                      ? "Ver menos"
                                      : "Ver más"}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default TemplatesList;
